/**
 * Federation Ledger — GHOST vs ACTIVE org cards from
 * /api/federation/associations. Shows every trade org on the network
 * with role, industry, and lifecycle state. ACTIVE orgs reveal
 * committee count; GHOST orgs surface only bare identity.
 */
import { useEffect, useState } from "react";
import { Search, ShieldCheck, Ghost } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const TABS = [
  { key: "",       label: "All" },
  { key: "ACTIVE", label: "Active" },
  { key: "GHOST",  label: "Ghost" },
];

export default function FederationLedgerPanel({ accent = "#4EC1E4" }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [lifecycle, setLifecycle] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/federation/associations", {
          params: { q, lifecycle: lifecycle || undefined, limit: 30 },
        });
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        console.warn("[FederationLedgerPanel]", e?.message);
      }
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, lifecycle]);

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="federation-ledger-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
            Federated Association Network
          </div>
          <h2 className="font-display text-xl uppercase tracking-tight">
            {total.toLocaleString()} orgs · ledger
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {TABS.map((t) => (
            <Button
              key={t.key || "all"}
              type="button"
              variant={lifecycle === t.key ? "default" : "ghost"}
              className={
                "h-8 px-3 text-[11px] uppercase tracking-[0.18em] " +
                (lifecycle === t.key
                  ? "bg-white/10 text-white"
                  : "text-white/55 hover:text-white")
              }
              onClick={() => setLifecycle(t.key)}
              data-testid={`fed-tab-${t.key || "all"}`}
            >
              {t.label}
            </Button>
          ))}
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search org"
                   className="h-8 w-56 bg-black/40 border-white/15 pl-9 text-xs"
                   data-testid="fed-ledger-search" />
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-white/55 py-8 text-center">
          No orgs match this filter.
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
             data-testid="fed-ledger-grid">
          {items.map((o) => (
            <div key={o.id}
                 className="rounded-xl border border-white/10 bg-black/30 p-4 flex flex-col gap-2"
                 data-testid={`fed-org-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
                    {(o.association_type || "assoc").toUpperCase()}
                    {o.trade_org_industry ? ` · ${o.trade_org_industry}` : ""}
                  </div>
                  <div className="font-display text-lg leading-tight mt-0.5">
                    {o.brand_name}
                  </div>
                  <div className="text-[11px] text-white/45">{o.legal_name}</div>
                </div>
                {o.lifecycle === "ACTIVE" ? (
                  <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-emerald-300"
                        title="Verified + quorum met">
                    <ShieldCheck size={12}/> Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-amber-300"
                        title="Provisional / unverified">
                    <Ghost size={12}/> Ghost
                  </span>
                )}
              </div>
              {o.lifecycle === "ACTIVE" && (
                <div className="text-[11px] text-white/55">
                  {o.committee_count}/{o.committee_size} seats ·{" "}
                  {o.registration_number || "no reg #"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
